import { z } from 'zod'

// ProblemResponse was removed from the regenerated OpenAPI spec — failures are now modelled as
// discriminated outcome variants with their own typed *ProblemDetails. ApiError is the sole consumer
// of this shape, so the schema lives here rather than in the spec-anchored api-schemas.ts (same
// de-anchoring precedent as UserSchema). See ISS-7852 / ISS-8079.
const ProblemResponseSchema = z.object({
  title: z.string(),
  detail: z.string(),
  // RFC 9457's discriminator, which every problem the platform raises sets to its own subtype URI —
  // `pixee:problems/api/integrations/endpoint-moved-without-credentials`, say. Kept as a plain
  // string so `ApiError` stays generic; the caller that knows the endpoint compares it against the
  // URIs it handles. Optional because a body that is not one of the platform's problems, or one
  // written before the subtype existed, still parses for its title and detail.
  type: z.string().optional(),
  // Only some problem types report a machine-readable reason — `IntegrationValidationFailedProblem`
  // is the one this exists for. Kept as a plain string here so `ApiError` stays generic; the caller
  // that knows which endpoint answered narrows it to that endpoint's enum.
  reason: z.string().optional(),
})

export class ApiError extends Error {
  constructor(
    public status: number,
    bodyAsText?: string | object,
    public path?: string
  ) {
    super(`API request failed with status ${status}`)
    this.name = 'ApiError'
    this.status = status

    if (bodyAsText === undefined || bodyAsText === null) {
      return
    }

    if (typeof bodyAsText === 'object') {
      this.bodyAsText = JSON.stringify(bodyAsText, undefined, 2)
      this.parseProblemJson(bodyAsText)
      return
    }

    if (typeof bodyAsText === 'string') {
      this.bodyAsText = bodyAsText
      this.parseProblemJsonFromText(bodyAsText)
      return
    }

    console.error(
      `ApiError constructor expected bodyAsText to be a string or object but was a ${typeof bodyAsText}: ${bodyAsText}`
    )
  }

  public bodyAsText?: string
  public problemTitle?: string
  public problemDetail?: string
  public problemType?: string
  public problemReason?: string
  /**
   * The error body as an object, for the extension fields a problem subtype carries beyond the four
   * above — `IntegrationInUseProblem`'s `repositories` list, say. Left untyped for the same reason
   * `problemType` is a plain string: the caller that knows which endpoint answered is the one that
   * can narrow it, and it does so with that problem's own schema.
   */
  public problemBody?: object

  private parseProblemJsonFromText(text: string) {
    try {
      const parsed: unknown = JSON.parse(text)
      if (typeof parsed === 'object' && parsed !== null) {
        this.parseProblemJson(parsed)
      }
    } catch {
      // Not valid JSON — leave problemTitle/problemDetail undefined
    }
  }

  private parseProblemJson(body: object) {
    this.problemBody = body
    const result = ProblemResponseSchema.safeParse(body)
    if (result.success) {
      this.problemTitle = result.data.title
      this.problemDetail = result.data.detail
      this.problemType = result.data.type
      this.problemReason = result.data.reason
    }
  }
}
